// src/components/RecoveredPassCard.jsx
import React, { useState, useRef } from 'react';
import QrCodeWithLogo from './QrCodeWithLogo';
import { generatePassImage } from '../utils/canvasPassGenerator';

/**
 * Tarjeta de Pase Recuperado (HU-04)
 * Muestra el resumen del boleto registrado (nombre, categoría, folio y código de tómbola)
 * y permite abrir el pase interactivo completo o descargar el PNG en alta resolución directamente.
 */
export default function RecoveredPassCard({ pass, onViewPass }) {
  const qrRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  if (!pass) return null;

  const paddedFolio = (pass.folio || pass.ticketFolio || '0000').toString().padStart(4, '0');
  const isVip = (pass.category || '').toUpperCase() === 'VIP';
  const raffleCode = pass.raffleTicketCode || `#XOLO-${paddedFolio}`;
  const qrPayload = pass.verificationToken || raffleCode || `XOLO-2026-${paddedFolio}`;

  const handleDownloadDirect = async (e) => {
    e.stopPropagation();
    setIsDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(false);

    try {
      await generatePassImage(pass, qrRef.current);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 5000);
    } catch (err) {
      console.error('[RecoveredPassCard] Error al descargar pase:', err);
      setDownloadError('Error al generar la imagen. Intenta desde la vista completa.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={`recovered-pass-card p-3 p-md-4 rounded-4 mb-3 position-relative ${
        isVip ? 'border-white' : 'border-danger shadow-danger-sm'
      }`}
      style={{
        backgroundColor: '#111116',
        border: isVip ? '1.5px solid rgba(255, 255, 255, 0.45)' : '1.5px solid rgba(220, 38, 38, 0.45)',
        boxShadow: isVip
          ? '0 8px 24px rgba(255, 255, 255, 0.08)'
          : '0 8px 24px rgba(220, 38, 38, 0.12)',
        transition: 'transform 0.2s ease, border-color 0.2s ease'
      }}
    >
      {/* QR Oculto en DOM para permitir la descarga directa sin abrir el modal */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }} aria-hidden="true">
        <QrCodeWithLogo
          ref={qrRef}
          id={`hidden-qr-${paddedFolio}`}
          value={qrPayload}
          size={240}
          logoSrc="/img/Big.png"
        />
      </div>

      {/* CABECERA DE LA TARJETA */}
      <div className="d-flex align-items-center justify-content-between mb-3 border-bottom border-secondary border-opacity-25 pb-2">
        <div className="d-flex align-items-center gap-2">
          <span
            className={`badge px-3 py-2 fw-bold text-uppercase ${
              isVip ? 'bg-white text-dark' : 'bg-danger text-white'
            }`}
            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
          >
            {isVip ? '★ VIP EXCLUSIVE' : 'ACCESO GENERAL'}
          </span>
          <span className="badge bg-success text-white px-2 py-1 fw-bold shadow-sm border border-success">
            <i className="bi bi-patch-check-fill me-1"></i> Registrado
          </span>
        </div>

        <span className="text-secondary small font-monospace">
          #{paddedFolio}
        </span>
      </div>

      {/* CUERPO: NOMBRE Y DATOS DEL AFICIONADO */}
      <div className="mb-3">
        <div className="d-flex align-items-start justify-content-between">
          <div>
            <span className="text-secondary text-uppercase fw-semibold d-block" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>
              Titular del Acceso
            </span>
            <h3 className="h5 text-white fw-bold mb-1 text-truncate" style={{ maxWidth: '300px' }}>
              {pass.fullName || 'Aficionado Xoloitzcuintle'}
            </h3>
            <span className="text-secondary small d-block">
              {pass.phone && (
                <span className="me-3">
                  <i className="bi bi-telephone-fill me-1 text-secondary"></i>
                  {pass.phone}
                </span>
              )}
              {pass.email && (
                <span>
                  <i className="bi bi-envelope-fill me-1 text-secondary"></i>
                  {pass.email}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* BLOQUE DE FOLIO & TÓMBOLA PERRONA */}
      <div className="row g-2 mb-3">
        <div className="col-6">
          <div
            className="p-2 rounded-3 text-center h-100"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          >
            <span className="text-secondary text-uppercase fw-semibold d-block mb-1" style={{ fontSize: '0.65rem' }}>
              Folio Boleto
            </span>
            <span className="font-monospace fw-black text-white fs-5">
              #{paddedFolio}
            </span>
          </div>
        </div>

        <div className="col-6">
          <div
            className="p-2 rounded-3 text-center h-100"
            style={{
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid rgba(220, 38, 38, 0.35)'
            }}
          >
            <span className="text-danger text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.65rem' }}>
              Tómbola Perrona
            </span>
            <span className="font-monospace fw-black text-white fs-6">
              {raffleCode}
            </span>
          </div>
        </div>
      </div>

      {/* ALERTAS LOCALES DE DESCARGA */}
      {downloadSuccess && (
        <div className="alert alert-success bg-success bg-opacity-20 border-success text-white py-1 px-2 mb-3 small animate-fade-in text-center">
          <i className="bi bi-check-circle-fill me-1 text-success"></i> ¡Pase #{paddedFolio} descargado correctamente!
        </div>
      )}

      {downloadError && (
        <div className="alert alert-danger bg-danger bg-opacity-20 border-danger text-white py-1 px-2 mb-3 small text-center">
          <i className="bi bi-exclamation-triangle-fill me-1"></i> {downloadError}
        </div>
      )}

      {/* BOTONES DE ACCIÓN: VER PASE Y DESCARGAR PNG */}
      <div className="row g-2 pt-1">
        <div className="col-12 col-sm-6">
          <button
            type="button"
            className="btn btn-xolo-outline w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
            onClick={() => onViewPass && onViewPass(pass)}
            title="Ver pase digital interactivo con código QR"
          >
            <i className="bi bi-qr-code text-white fs-6"></i>
            <span>Ver Pase</span>
          </button>
        </div>

        <div className="col-12 col-sm-6">
          <button
            type="button"
            className="btn btn-xolo-primary w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
            onClick={handleDownloadDirect}
            disabled={isDownloading}
            title="Descargar imagen oficial en alta resolución"
          >
            {isDownloading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Generando...</span>
              </>
            ) : (
              <>
                <i className="bi bi-download fs-6"></i>
                <span>Descargar PNG</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
